import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { SkillsActions } from './slice'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import getSkillsContent from '../fetch/get-skills-content'
import postInitConversationWithSkill from '../fetch/post-init-conversation-with-skill'
import { SkillSource } from '@/analytics/schema/events.schema'

export const skillsListenerMiddleware = createListenerMiddleware()
const startListener = skillsListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

/**
 * Skills: Initialize Conversation With Skill
 */
startListener({
  actionCreator: SkillsActions.initializeConversationWithSkill,
  effect: async (action, listenerApi) => {
    const {
      conversationId,
      skill,
      skillSource = SkillSource.quick_action,
    } = action.payload as {
      conversationId: string
      skill: string
      skillSource?: SkillSource
    }

    try {
      // Set the skill initialization loading state
      listenerApi.dispatch(SkillsActions.setSkillInitLoading({ skill }))
      listenerApi.dispatch(SkillsActions.setSkillInitError({ skill: null }))
      listenerApi.dispatch(
        SkillsActions.setPendingSkillForConversation({
          conversationId,
          skill,
          skillSource,
        })
      )

      // DEV TESTING
      // // Add a delay
      // await new Promise((resolve) => setTimeout(resolve, 3000))
      // throw new Error('Simulated error for testing')

      // Send the request to the server
      await postInitConversationWithSkill(conversationId, skill)
    } catch (error) {
      // Set the skill initialization error state
      listenerApi.dispatch(SkillsActions.setSkillInitError({ skill }))
    } finally {
      // Reset the skill initialization loading state
      listenerApi.dispatch(SkillsActions.setSkillInitLoading({ skill: null }))
    }

    // Refresh the conversation
    listenerApi.dispatch(AgentConversationsActions.fetchConversationData({ conversationId, forceRefresh: true }))
  },
})

/**
 * Skills: Fetch Skills Content
 */
startListener({
  actionCreator: SkillsActions.fetchSkillsContent,
  effect: async (_action, listenerApi) => {
    const { dispatch, getState } = listenerApi

    // Get the existing state
    const state = getState()
    const currentData = state.skillsState.skillsContentData
    const currentLoading = state.skillsState.skillsContentLoading

    // Avoid reloading if the data is already present or if currently loading
    if (currentLoading || currentData.skills.length > 0) {
      return
    }

    dispatch(SkillsActions.setSkillsContentLoading(true))
    dispatch(SkillsActions.setSkillsContentError(false))
    try {
      console.log('Fetching skills content...')
      const skillsContent = await getSkillsContent()
      console.log('Skills content fetched successfully:', skillsContent)
      dispatch(SkillsActions.setSkillsContentData(skillsContent))
    } catch (e) {
      console.error('Error fetching skills content:', e)
      dispatch(SkillsActions.setSkillsContentError(true))
    } finally {
      dispatch(SkillsActions.setSkillsContentLoading(false))
    }
  },
})
